import React from "react"
import { RouteObject, Outlet, To, Params } from "react-router"
import studyRouteNames from "src/pages/study/study-route-names"
import Study from "src/pages/study/study"
import { createReportRoutes } from "src/pages/study/reports/reports-routes"
import {
  ComplianceDimensionCrumb,
  Crumb,
} from "src/pages/study/components/breadcrumbs"
import { getStudyQueryOptions } from "src/pages/study/use-sponsor-study"
import CheckValidVaultSession from "src/check-valid-vault-session"
import { Redirect } from "src/router/routes"
import { getParticipantEventsAndGroupsQueryOptions } from "src/pages/study/participants/participants.model"
import { UseQueryOptions } from "@tanstack/react-query"
import {
  getSiteAndParticipantMetricsOptions,
  listSiteMetricsOptions,
} from "src/pages/study/sites/sites.model"

export interface RouteHandle {
  crumb: (args: { to: To; data: any; disabled: boolean }) => React.ReactNode
  getQueryOptions?: (
    params: Params<string>
  ) => UseQueryOptions<any, any, any, any>
}

export const createStudyRoutes = (): RouteObject[] => [
  {
    element: <Study />,
    path: studyRouteNames.study.route,
    handle: {
      crumb: ({ to, data, disabled }) => (
        <Crumb to={to} data={data?.studyName} disabled={disabled} />
      ),
      getQueryOptions: (params) =>
        getStudyQueryOptions(params.globalSponsorStudyId as string),
    } satisfies RouteHandle,
    children: [
      {
        element: (
          <>
            <CheckValidVaultSession /> <Outlet />
          </>
        ),
        children: [
          {
            path: studyRouteNames.studyOverview.route,
            lazy: () => import("src/pages/study/overview/overview"),
            index: true,
          },
          {
            path: studyRouteNames.dataChanges.route,
            lazy: () => import("src/pages/study/data-changes/data-changes"),
            handle: {
              crumb: ({ to, disabled }) => (
                <Crumb
                  to={to}
                  disabled={disabled}
                  messageId="studyDataChanges.dataChanges"
                />
              ),
            } satisfies RouteHandle,
          },
          {
            path: studyRouteNames.sites.route,
            handle: {
              crumb: ({ to, disabled }) => (
                <Crumb
                  to={to}
                  disabled={disabled}
                  messageId="sponsorOverview.sites"
                />
              ),
              getQueryOptions: (params) =>
                listSiteMetricsOptions({
                  globalSponsorStudyId: params.globalSponsorStudyId as string,
                }),
            } satisfies RouteHandle,
            children: [
              {
                index: true,
                lazy: () => import("src/pages/study/sites/sites"),
              },
              {
                path: studyRouteNames.site.route,
                handle: {
                  crumb: ({ to, data, disabled }) => (
                    <Crumb
                      to={to}
                      messageArgs={{
                        siteNumber:
                          data?.siteParticipantMetrics.siteSummaryMetrics
                            .siteNumber,
                      }}
                      messageId="sponsorSiteOverview.pageTitle"
                      disabled={disabled}
                    />
                  ),
                  getQueryOptions: (params) =>
                    getSiteAndParticipantMetricsOptions({
                      globalSponsorStudyId:
                        params.globalSponsorStudyId as string,
                      siteId: params.siteId as string,
                    }),
                } satisfies RouteHandle,
                children: [
                  {
                    path: studyRouteNames.siteOverview.route,
                    lazy: () => import("src/pages/study/sites/site-overview"),
                  },
                  {
                    path: studyRouteNames.participants.route,
                    children: [
                      {
                        path: studyRouteNames.participant.route,
                        lazy: () =>
                          import("src/pages/study/participants/participant-overview"),
                        handle: {
                          crumb: ({ to, data, disabled }) => (
                            <Crumb
                              to={to}
                              disabled={disabled}
                              messageId="studyHome.participant.title"
                              messageArgs={{
                                participantNumber: data?.participantNumber,
                              }}
                            />
                          ),
                          getQueryOptions: (params) =>
                            getParticipantEventsAndGroupsQueryOptions({
                              studyParticipantId:
                                params.participantId as string,
                            }),
                        } satisfies RouteHandle,
                      },
                    ],
                  },
                  {
                    index: true,
                    element: (
                      <Redirect to={studyRouteNames.siteOverview.route} />
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: studyRouteNames.compliance.route,
            handle: {
              crumb: ({ to }) => (
                <Crumb
                  to={to}
                  disabled
                  messageId="sponsorStudyNav.compliance"
                />
              ),
            } satisfies RouteHandle,
            children: [
              {
                path: studyRouteNames.complianceDimension.route,
                lazy: () =>
                  import("src/pages/study/compliance/compliance-layout"),
                handle: {
                  crumb: ({ to, disabled }) => (
                    <ComplianceDimensionCrumb to={to} disabled={disabled} />
                  ),
                } satisfies RouteHandle,
              },
              {
                index: true,
                element: (
                  <Redirect to={studyRouteNames.complianceStudy.route} />
                ),
              },
            ],
          },
          ...createReportRoutes(),
          {
            index: true,
            element: <Redirect to={studyRouteNames.studyOverview.route} />,
          },
        ],
      },
    ],
  },
]
